// The planner's work in progress, held in memory and nowhere else.
//
// The planner used to autosave to browser storage so a reload would not cost
// an afternoon. That convenience is not worth what it costs on a shared or
// borrowed computer: the lesson stayed on the machine, and the next person to
// open the site found it in the form. sessionStorage was not enough either —
// browsers restore it when a closed tab is reopened or a session is restored
// at startup, which is exactly the case being guarded against.
//
// So nothing is stored. The plan lives in React state, and this module holds
// the reference so the AI Prompt page and the companion-pack export can read
// what the planner currently has. Both are tabs in the same page, so a module
// variable is all the hand-off needs; it dies with the page and cannot be
// restored by anything.
//
// The way to keep a lesson is Save JSON, and the way to get it back is Upload
// JSON. MainApp already warns on unload that work must be exported.

let snapshot = null;

/** Record what the planner currently holds, for the pages that read it. */
export const setPlannerSnapshot = (plan) => {
  snapshot = plan && typeof plan === 'object' ? plan : null;
};

/** The planner's current plan, or null when nothing has been entered. */
export const getPlannerSnapshot = () => snapshot;

export const clearPlannerSnapshot = () => {
  snapshot = null;
};

/** Keys earlier versions wrote, in the two stores they could have landed in. */
const LEGACY_KEYS = ['boppps-planner-data', 'boppps-planner-field-sizes'];

/**
 * Delete anything an earlier version of the planner left in browser storage.
 *
 * Run on mount rather than on demand: the copies accumulating on shared
 * machines are the whole problem, and the only moment this app can reach them
 * is when someone opens it on that machine. Deleting rather than adopting is
 * deliberate — a plan that outlived its tab is precisely what should not come
 * back.
 */
export const purgeStoredPlannerData = () => {
  if (typeof window === 'undefined') return;
  ['localStorage', 'sessionStorage'].forEach((name) => {
    let store;
    try {
      // Reading the property is itself what throws where storage is disabled.
      store = window[name];
    } catch (error) {
      return;
    }
    if (!store || typeof store.removeItem !== 'function') return;
    LEGACY_KEYS.forEach((key) => {
      try {
        store.removeItem(key);
      } catch (error) {
        // Nothing else to try; there is no state to keep consistent.
      }
    });
  });
};

export const __testing = { LEGACY_KEYS };
